import React from 'react';
import { Form } from 'react-bootstrap';

const conversionFactors = {
    // Length conversions
    in_to_mm: 25.4,
    in_to_cm: 2.54,
    ft_to_m: 0.3048,

    // Pressure/Strength conversions
    psi_to_MPa: 0.00689476,
    psi_to_kPa: 6.89476,
    pci_to_kN_m3: 271.447,

    // Mass conversions
    lb_to_kg: 0.453592,
    ton_to_kg: 907.185,

    // Thermal expansion conversions
    thermal_in_F_to_m_C: 1.8 // (10^-6 in/in/F) to (10^-6 m/m/°C)
};

function PanelJPCP({
    onDataChange,
    data,
    unitSystem,
    convertPressure,
    displayPressureUnit,
    convertModulus,
    displayModulusUnit,
    convertLength,
    displayLengthUnit,
    handleBlur,
    getFieldState,
    fieldPrefix = ""
    }) {

    // Helper function to convert thermal expansion coefficient
    const convertThermalExpansion = (value) => {
        return unitSystem === 'imperial' ? value : value / conversionFactors.thermal_in_F_to_m_C;
    };

    // Helper function to display thermal expansion unit
    const displayThermalExpansionUnit = () => {
        return unitSystem === 'imperial' ? '10^-6 in/in/F' : '10^-6 m/m/°C';
    };

    // Helper function to convert feet to meters for length fields
    const convertLengthFt = (value) => {
        return unitSystem === 'imperial' ? value : value * conversionFactors.ft_to_m;
    };

    // Helper function to display length unit for ft fields
    const displayLengthUnitFt = () => {
        return unitSystem === 'imperial' ? 'ft' : 'm';
    };

    return (
    <div className="pd-table-responsive">
        <table>
            <thead>
                <tr>
                    <th className="pd__label pd__table-head">Parameters</th>
                    <th className="pd__label pd__table-head">Min</th>
                    <th className="pd__label pd__table-head">Mean</th>
                    <th className="pd__label pd__table-head">Max</th>
                    <th className="pd__label pd__table-head">Distribution</th>
                </tr>
            </thead>
        <tbody>
            {/* PCC Elastic Modulus  */}
            <tr>
                <td className="pd__label">PCC Elastic Modulus ({displayModulusUnit()})</td>
                <td>
                    <Form.Control
                        type="number"
                        size="sm"
                        className="pd-input-sm"
                        name="jpcpDesign.pccElasticModulus.min"
                        value={parseFloat(parseFloat(convertModulus(data.jpcpDesign.pccElasticModulus.min)).toFixed(2))}
                        onChange={e => onDataChange({
                            target: {
                            name: "jpcpDesign.pccElasticModulus.min",
                            value: unitSystem === 'imperial'
                                ? e.target.value
                                : e.target.value / conversionFactors.pci_to_kN_m3
                            }
                        })}
                        onBlur={() => handleBlur(`${fieldPrefix}jpcpDesign.pccElasticModulus.min`, data.jpcpDesign.pccElasticModulus.min, 'number')}
                        isInvalid={getFieldState(`${fieldPrefix}jpcpDesign.pccElasticModulus.min`).isInvalid}
                    />
                </td>
                <td>
                    <Form.Control
                        type="number"
                        size="sm"
                        className="pd-input-sm"
                        name="jpcpDesign.pccElasticModulus.mean"
                        value={parseFloat(parseFloat(convertModulus(data.jpcpDesign.pccElasticModulus.mean)).toFixed(2))}
                        onChange={e => onDataChange({
                            target: {
                                name: "jpcpDesign.pccElasticModulus.mean",
                                value: unitSystem === 'imperial'
                                    ? e.target.value
                                    : e.target.value / conversionFactors.pci_to_kN_m3
                            }
                        })}
                        onBlur={() => handleBlur(`${fieldPrefix}jpcpDesign.pccElasticModulus.mean`, data.jpcpDesign.pccElasticModulus.mean, 'number')}
                        isInvalid={getFieldState(`${fieldPrefix}jpcpDesign.pccElasticModulus.mean`).isInvalid}
                    />
                </td>
                <td>
                    <Form.Control
                        type="number"
                        size="sm"
                        className="pd-input-sm"
                        name="jpcpDesign.pccElasticModulus.max"
                        value={parseFloat(parseFloat(convertModulus(data.jpcpDesign.pccElasticModulus.max)).toFixed(2))}
                        onChange={e => onDataChange({
                            target: {
                                name: "jpcpDesign.pccElasticModulus.max",
                                value: unitSystem === 'imperial'
                                    ? e.target.value
                                    : e.target.value / conversionFactors.pci_to_kN_m3
                            }
                        })}
                        onBlur={() => handleBlur(`${fieldPrefix}jpcpDesign.pccElasticModulus.max`, data.jpcpDesign.pccElasticModulus.max, 'number')}
                        isInvalid={getFieldState(`${fieldPrefix}jpcpDesign.pccElasticModulus.max`).isInvalid}
                    />
                </td>
                <td>
                    <Form.Select
                        size="sm"
                        className="pd-select-md"
                        name="jpcpDesign.pccElasticModulus.distribution"
                        value={data.jpcpDesign.pccElasticModulus.distribution}
                        onChange={onDataChange}>
                            <option value="Uniform" selected>Uniform</option>
                            <option value="Normal">Normal</option>
                            <option value="Lognormal">Lognormal</option>
                            <option value="Triangular">Triangular</option>
                    </Form.Select>
                </td>
            </tr>

            {/* PCC Comp. Strength (psi) */}
            <tr>
                <td className="pd__label">PCC Comp. Strength ({displayPressureUnit()})</td>
                <td>
                    <Form.Control
                        type="number"
                        size="sm"
                        className="pd-input-sm"
                        name="jpcpDesign.pccCompStrength.min"
                        disabled={data.design.usePhaseComponent}
                        value={parseFloat(parseFloat(convertPressure(data.jpcpDesign.pccCompStrength.min)).toFixed(2))}
                        onChange={e => onDataChange({
                            target: {
                                name: "jpcpDesign.pccCompStrength.min",
                                value: unitSystem === 'imperial'
                                    ? e.target.value
                                    : e.target.value / conversionFactors.psi_to_MPa
                            }
                        })}
                        onBlur={() => handleBlur(`${fieldPrefix}jpcpDesign.pccCompStrength.min`, data.jpcpDesign.pccCompStrength.min, 'number')}
                        isInvalid={getFieldState(`${fieldPrefix}jpcpDesign.pccCompStrength.min`).isInvalid}
                    />
                </td>
                <td>
                    <Form.Control
                        type="number"
                        size="sm"
                        className="pd-input-sm"
                        name="jpcpDesign.pccCompStrength.mean"
                        disabled={data.design.usePhaseComponent}
                        value={parseFloat(parseFloat(convertPressure(data.jpcpDesign.pccCompStrength.mean)).toFixed(2))}
                        onChange={e => onDataChange({
                            target: {
                                name: "jpcpDesign.pccCompStrength.mean",
                                value: unitSystem === 'imperial'
                                    ? e.target.value
                                    : e.target.value / conversionFactors.psi_to_MPa
                            }
                        })}
                        onBlur={() => handleBlur(`${fieldPrefix}jpcpDesign.pccCompStrength.mean`, data.jpcpDesign.pccCompStrength.mean, 'number')}
                        isInvalid={getFieldState(`${fieldPrefix}jpcpDesign.pccCompStrength.mean`).isInvalid}
                    />
                </td>
                <td>
                    <Form.Control
                        type="number"
                        size="sm"
                        className="pd-input-sm"
                        name="jpcpDesign.pccCompStrength.max"
                        disabled={data.design.usePhaseComponent}
                        value={parseFloat(parseFloat(convertPressure(data.jpcpDesign.pccCompStrength.max)).toFixed(2))}
                        onChange={e => onDataChange({
                            target: {
                                name: "jpcpDesign.pccCompStrength.max",
                                value: unitSystem === 'imperial'
                                    ? e.target.value
                                    : e.target.value / conversionFactors.psi_to_MPa
                            }
                        })}
                        onBlur={() => handleBlur(`${fieldPrefix}jpcpDesign.pccCompStrength.max`, data.jpcpDesign.pccCompStrength.max, 'number')}
                        isInvalid={getFieldState(`${fieldPrefix}jpcpDesign.pccCompStrength.max`).isInvalid}
                    />
                </td>
                <td>
                    <Form.Select
                        size="sm"
                        className="pd-select-md"
                        name="jpcpDesign.pccCompStrength.distribution"
                        disabled={data.design.usePhaseComponent}
                        value={data.jpcpDesign.pccCompStrength.distribution}
                        onChange={onDataChange}>
                            <option value="Uniform" selected>Uniform</option>
                            <option value="Normal">Normal</option>
                            <option value="Lognormal">Lognormal</option>
                            <option value="Triangular">Triangular</option>
                    </Form.Select>
                </td>
            </tr>

            {/* PCC Modules of Rupture (psi) */}
            <tr>
                <td className="pd__label">PCC Modules of Rupture ({displayPressureUnit()})</td>
                <td>
                    <Form.Control
                        type="number"
                        size="sm"
                        className="pd-input-sm"
                        name="jpcpDesign.pccModulesOfRupture.min"
                        disabled={!data.design.usePhaseComponent}
                        value={parseFloat(parseFloat(convertPressure(data.jpcpDesign.pccModulesOfRupture.min)).toFixed(2))}
                        onChange={e => onDataChange({
                            target: {
                                name: "jpcpDesign.pccModulesOfRupture.min",
                                value: unitSystem === 'imperial'
                                    ? e.target.value
                                    : e.target.value / conversionFactors.psi_to_MPa
                            }
                        })}
                        onBlur={() => handleBlur(`${fieldPrefix}jpcpDesign.pccModulesOfRupture.min`, data.jpcpDesign.pccModulesOfRupture.min, 'number')}
                        isInvalid={getFieldState(`${fieldPrefix}jpcpDesign.pccModulesOfRupture.min`).isInvalid}
                    />
                </td>
                <td>
                    <Form.Control
                        type="number"
                        size="sm"
                        className="pd-input-sm"
                        name="jpcpDesign.pccModulesOfRupture.mean"
                        disabled={!data.design.usePhaseComponent}
                        value={parseFloat(parseFloat(convertPressure(data.jpcpDesign.pccModulesOfRupture.mean)).toFixed(2))}
                        onChange={e => onDataChange({
                            target: {
                                name: "jpcpDesign.pccModulesOfRupture.mean",
                                value: unitSystem === 'imperial'
                                    ? e.target.value
                                    : e.target.value / conversionFactors.psi_to_MPa
                            }
                        })}
                        onBlur={() => handleBlur(`${fieldPrefix}jpcpDesign.pccModulesOfRupture.mean`, data.jpcpDesign.pccModulesOfRupture.mean, 'number')}
                        isInvalid={getFieldState(`${fieldPrefix}jpcpDesign.pccModulesOfRupture.mean`).isInvalid}
                    />
                </td>
                <td>
                    <Form.Control
                        type="number"
                        size="sm"
                        className="pd-input-sm"
                        name="jpcpDesign.pccModulesOfRupture.max"
                        disabled={!data.design.usePhaseComponent}
                        value={parseFloat(parseFloat(convertPressure(data.jpcpDesign.pccModulesOfRupture.max)).toFixed(2))}
                        onChange={e => onDataChange({
                            target: {
                                name: "jpcpDesign.pccModulesOfRupture.max",
                                value: unitSystem === 'imperial'
                                    ? e.target.value
                                    : e.target.value / conversionFactors.psi_to_MPa
                            }
                        })}
                        onBlur={() => handleBlur(`${fieldPrefix}jpcpDesign.pccModulesOfRupture.max`, data.jpcpDesign.pccModulesOfRupture.max, 'number')}
                        isInvalid={getFieldState(`${fieldPrefix}jpcpDesign.pccModulesOfRupture.max`).isInvalid}
                    />
                </td>
                <td>
                    <Form.Select
                        size="sm"
                        className="pd-select-md"
                        name="jpcpDesign.pccModulesOfRupture.distribution"
                        disabled={!data.design.usePhaseComponent}
                        value={data.jpcpDesign.pccModulesOfRupture.distribution}
                        onChange={onDataChange}>
                            <option value="Uniform" selected>Uniform</option>
                            <option value="Normal">Normal</option>
                            <option value="Lognormal">Lognormal</option>
                            <option value="Triangular">Triangular</option>
                    </Form.Select>
                </td>
            </tr>

            {/* Coefficient of Thermal Expansion - NOW WITH UNIT CONVERSION */}
            <tr>
                <td className="pd__label">Coefficient of Thermal Expansion ({displayThermalExpansionUnit()})</td>
                <td>
                    <Form.Control
                        type="number"
                        size="sm"
                        className="pd-input-sm"
                        name="jpcpDesign.coefficientOfThermalExpansion.min"
                        value={parseFloat(parseFloat(convertThermalExpansion(data.jpcpDesign.coefficientOfThermalExpansion.min)).toFixed(2))}
                        onChange={e => onDataChange({
                            target: {
                                name: "jpcpDesign.coefficientOfThermalExpansion.min",
                                value: unitSystem === 'imperial'
                                    ? e.target.value
                                    : e.target.value * conversionFactors.thermal_in_F_to_m_C
                            }
                        })}
                        onBlur={() => handleBlur(`${fieldPrefix}jpcpDesign.coefficientOfThermalExpansion.min`, data.jpcpDesign.coefficientOfThermalExpansion.min, 'number')}
                        isInvalid={getFieldState(`${fieldPrefix}jpcpDesign.coefficientOfThermalExpansion.min`).isInvalid}
                    />
                </td>
                <td>
                    <Form.Control
                        type="number"
                        size="sm"
                        className="pd-input-sm"
                        name="jpcpDesign.coefficientOfThermalExpansion.mean"
                        value={parseFloat(parseFloat(convertThermalExpansion(data.jpcpDesign.coefficientOfThermalExpansion.mean)).toFixed(2))}
                        onChange={e => onDataChange({
                            target: {
                                name: "jpcpDesign.coefficientOfThermalExpansion.mean",
                                value: unitSystem === 'imperial'
                                    ? e.target.value
                                    : e.target.value * conversionFactors.thermal_in_F_to_m_C
                            }
                        })}
                        onBlur={() => handleBlur(`${fieldPrefix}jpcpDesign.coefficientOfThermalExpansion.mean`, data.jpcpDesign.coefficientOfThermalExpansion.mean, 'number')}
                        isInvalid={getFieldState(`${fieldPrefix}jpcpDesign.coefficientOfThermalExpansion.mean`).isInvalid}
                    />
                </td>
                <td>
                    <Form.Control
                        type="number"
                        size="sm"
                        className="pd-input-sm"
                        name="jpcpDesign.coefficientOfThermalExpansion.max"
                        value={parseFloat(parseFloat(convertThermalExpansion(data.jpcpDesign.coefficientOfThermalExpansion.max)).toFixed(2))}
                        onChange={e => onDataChange({
                            target: {
                                name: "jpcpDesign.coefficientOfThermalExpansion.max",
                                value: unitSystem === 'imperial'
                                    ? e.target.value
                                    : e.target.value * conversionFactors.thermal_in_F_to_m_C
                            }
                        })}
                        onBlur={() => handleBlur(`${fieldPrefix}jpcpDesign.coefficientOfThermalExpansion.max`, data.jpcpDesign.coefficientOfThermalExpansion.max, 'number')}
                        isInvalid={getFieldState(`${fieldPrefix}jpcpDesign.coefficientOfThermalExpansion.max`).isInvalid}
                    />
                </td>
                <td>
                    <Form.Select
                        size="sm"
                        className="pd-select-md"
                        name="jpcpDesign.coefficientOfThermalExpansion.distribution"
                        value={data.jpcpDesign.coefficientOfThermalExpansion.distribution}
                        onChange={onDataChange}>
                            <option value="Uniform" selected>Uniform</option>
                            <option value="Normal">Normal</option>
                            <option value="Lognormal">Lognormal</option>
                            <option value="Triangular">Triangular</option>
                    </Form.Select>
                </td>
            </tr>

            {/* Joint Spacing - NOW WITH UNIT CONVERSION */}
            <tr>
                <td className="pd__label">Joint Spacing ({displayLengthUnitFt()})</td>
                <td>
                    <Form.Control
                        type="number"
                        size="sm"
                        className="pd-input-sm"
                        name="jpcpDesign.jointSpacing.min"
                        value={parseFloat(parseFloat(convertLengthFt(data.jpcpDesign.jointSpacing.min)).toFixed(2))}
                        onChange={e => onDataChange({
                            target: {
                                name: "jpcpDesign.jointSpacing.min",
                                value: unitSystem === 'imperial'
                                    ? e.target.value
                                    : e.target.value / conversionFactors.ft_to_m
                            }
                        })}
                        onBlur={() => handleBlur(`${fieldPrefix}jpcpDesign.jointSpacing.min`, data.jpcpDesign.jointSpacing.min, 'number')}
                        isInvalid={getFieldState(`${fieldPrefix}jpcpDesign.jointSpacing.min`).isInvalid}
                    />
                </td>
                <td>
                    <Form.Control
                        type="number"
                        size="sm"
                        className="pd-input-sm"
                        name="jpcpDesign.jointSpacing.mean"
                        value={parseFloat(parseFloat(convertLengthFt(data.jpcpDesign.jointSpacing.mean)).toFixed(2))}
                        onChange={e => onDataChange({
                            target: {
                                name: "jpcpDesign.jointSpacing.mean",
                                value: unitSystem === 'imperial'
                                    ? e.target.value
                                    : e.target.value / conversionFactors.ft_to_m
                            }
                        })}
                        onBlur={() => handleBlur(`${fieldPrefix}jpcpDesign.jointSpacing.mean`, data.jpcpDesign.jointSpacing.mean, 'number')}
                        isInvalid={getFieldState(`${fieldPrefix}jpcpDesign.jointSpacing.mean`).isInvalid}
                    />
                </td>
                <td>
                    <Form.Control
                        type="number"
                        size="sm"
                        className="pd-input-sm"
                        name="jpcpDesign.jointSpacing.max"
                        value={parseFloat(parseFloat(convertLengthFt(data.jpcpDesign.jointSpacing.max)).toFixed(2))}
                        onChange={e => onDataChange({
                            target: {
                                name: "jpcpDesign.jointSpacing.max",
                                value: unitSystem === 'imperial'
                                    ? e.target.value
                                    : e.target.value / conversionFactors.ft_to_m
                            }
                        })}
                        onBlur={() => handleBlur(`${fieldPrefix}jpcpDesign.jointSpacing.max`, data.jpcpDesign.jointSpacing.max, 'number')}
                        isInvalid={getFieldState(`${fieldPrefix}jpcpDesign.jointSpacing.max`).isInvalid}
                    />
                </td>
                <td>
                    <Form.Select
                        size="sm"
                        className="pd-select-md"
                        name="jpcpDesign.jointSpacing.distribution"
                        value={data.jpcpDesign.jointSpacing.distribution}
                        onChange={onDataChange}>
                            <option value="Uniform" selected>Uniform</option>
                            <option value="Normal">Normal</option>
                            <option value="Lognormal">Lognormal</option>
                            <option value="Triangular">Triangular</option>
                    </Form.Select>
                </td>
            </tr>

            {/* Base Resilient Modulus (ESB) - NOW WITH PROPER UNIT CONVERSION */}
            <tr>
                <td className="pd__label">Base Resilient Modulus (ESB): {displayPressureUnit()}</td>
                <td>
                    <Form.Control
                        type="number"
                        size="sm"
                        className="pd-input-sm"
                        name="jpcpDesign.baseResilientModulus.min"
                        value={parseFloat(parseFloat(convertPressure(data.jpcpDesign.baseResilientModulus.min)).toFixed(2))}
                        onChange={e => onDataChange({
                            target: {
                                name: "jpcpDesign.baseResilientModulus.min",
                                value: unitSystem === 'imperial'
                                    ? e.target.value
                                    : e.target.value / conversionFactors.psi_to_MPa
                            }
                        })}
                        onBlur={() => handleBlur(`${fieldPrefix}jpcpDesign.baseResilientModulus.min`, data.jpcpDesign.baseResilientModulus.min, 'number')}
                        isInvalid={getFieldState(`${fieldPrefix}jpcpDesign.baseResilientModulus.min`).isInvalid}
                    />
                </td>
                <td>
                    <Form.Control
                        type="number"
                        size="sm"
                        className="pd-input-sm"
                        name="jpcpDesign.baseResilientModulus.mean"
                        value={parseFloat(parseFloat(convertPressure(data.jpcpDesign.baseResilientModulus.mean)).toFixed(2))}
                        onChange={e => onDataChange({
                            target: {
                                name: "jpcpDesign.baseResilientModulus.mean",
                                value: unitSystem === 'imperial'
                                    ? e.target.value
                                    : e.target.value / conversionFactors.psi_to_MPa
                            }
                        })}
                        onBlur={() => handleBlur(`${fieldPrefix}jpcpDesign.baseResilientModulus.mean`, data.jpcpDesign.baseResilientModulus.mean, 'number')}
                        isInvalid={getFieldState(`${fieldPrefix}jpcpDesign.baseResilientModulus.mean`).isInvalid}
                    />
                </td>
                <td>
                    <Form.Control
                        type="number"
                        size="sm"
                        className="pd-input-sm"
                        name="jpcpDesign.baseResilientModulus.max"
                        value={parseFloat(parseFloat(convertPressure(data.jpcpDesign.baseResilientModulus.max)).toFixed(2))}
                        onChange={e => onDataChange({
                            target: {
                                name: "jpcpDesign.baseResilientModulus.max",
                                value: unitSystem === 'imperial'
                                    ? e.target.value
                                    : e.target.value / conversionFactors.psi_to_MPa
                            }
                        })}
                        onBlur={() => handleBlur(`${fieldPrefix}jpcpDesign.baseResilientModulus.max`, data.jpcpDesign.baseResilientModulus.max, 'number')}
                        isInvalid={getFieldState(`${fieldPrefix}jpcpDesign.baseResilientModulus.max`).isInvalid}
                    />
                </td>
                <td>
                    <Form.Select
                        size="sm"
                        className="pd-select-md"
                        name="jpcpDesign.baseResilientModulus.distribution"
                        value={data.jpcpDesign.baseResilientModulus.distribution}
                        onChange={onDataChange}>
                            <option value="Uniform" selected>Uniform</option>
                            <option value="Normal">Normal</option>
                            <option value="Lognormal">Lognormal</option>
                            <option value="Triangular">Triangular</option>
                    </Form.Select>
                </td>
            </tr>

            {/* Subgrade Resilient Modulus (MR) - NOW WITH PROPER UNIT CONVERSION */}
            <tr>
                <td className="pd__label">Subgrade Resilient Modulus (MR): {displayPressureUnit()}</td>
                <td>
                    <Form.Control
                        type="number"
                        size="sm"
                        className="pd-input-sm"
                        name="jpcpDesign.subgradeResilientModulus.min"
                        value={parseFloat(parseFloat(convertPressure(data.jpcpDesign.subgradeResilientModulus.min)).toFixed(2))}
                        onChange={e => onDataChange({
                            target: {
                                name: "jpcpDesign.subgradeResilientModulus.min",
                                value: unitSystem === 'imperial'
                                    ? e.target.value
                                    : e.target.value / conversionFactors.psi_to_MPa
                            }
                        })}
                        onBlur={() => handleBlur(`${fieldPrefix}jpcpDesign.subgradeResilientModulus.min`, data.jpcpDesign.subgradeResilientModulus.min, 'number')}
                        isInvalid={getFieldState(`${fieldPrefix}jpcpDesign.subgradeResilientModulus.min`).isInvalid}
                    />
                </td>
                <td>
                    <Form.Control
                        type="number"
                        size="sm"
                        className="pd-input-sm"
                        name="jpcpDesign.subgradeResilientModulus.mean"
                        value={parseFloat(parseFloat(convertPressure(data.jpcpDesign.subgradeResilientModulus.mean)).toFixed(2))}
                        onChange={e => onDataChange({
                            target: {
                                name: "jpcpDesign.subgradeResilientModulus.mean",
                                value: unitSystem === 'imperial'
                                    ? e.target.value
                                    : e.target.value / conversionFactors.psi_to_MPa
                            }
                        })}
                        onBlur={() => handleBlur(`${fieldPrefix}jpcpDesign.subgradeResilientModulus.mean`, data.jpcpDesign.subgradeResilientModulus.mean, 'number')}
                        isInvalid={getFieldState(`${fieldPrefix}jpcpDesign.subgradeResilientModulus.mean`).isInvalid}
                    />
                </td>
                <td>
                    <Form.Control
                        type="number"
                        size="sm"
                        className="pd-input-sm"
                        name="jpcpDesign.subgradeResilientModulus.max"
                        value={parseFloat(parseFloat(convertPressure(data.jpcpDesign.subgradeResilientModulus.max)).toFixed(2))}
                        onChange={e => onDataChange({
                            target: {
                                name: "jpcpDesign.subgradeResilientModulus.max",
                                value: unitSystem === 'imperial'
                                    ? e.target.value
                                    : e.target.value / conversionFactors.psi_to_MPa
                            }
                        })}
                        onBlur={() => handleBlur(`${fieldPrefix}jpcpDesign.subgradeResilientModulus.max`, data.jpcpDesign.subgradeResilientModulus.max, 'number')}
                        isInvalid={getFieldState(`${fieldPrefix}jpcpDesign.subgradeResilientModulus.max`).isInvalid}
                    />
                </td>
                <td>
                    <Form.Select
                        size="sm"
                        className="pd-select-md"
                        name="jpcpDesign.subgradeResilientModulus.distribution"
                        value={data.jpcpDesign.subgradeResilientModulus.distribution}
                        onChange={onDataChange}>
                            <option value="Uniform" selected>Uniform</option>
                            <option value="Normal">Normal</option>
                            <option value="Lognormal">Lognormal</option>
                            <option value="Triangular">Triangular</option>
                    </Form.Select>
                </td>
            </tr>

            {/* Depth to Rigid Foundation - NOW WITH UNIT CONVERSION */}
            <tr>
                <td className="pd__label">Depth to Rigid Foundation ({displayLengthUnitFt()})</td>
                <td>
                    <Form.Control
                        type="number"
                        size="sm"
                        className="pd-input-sm"
                        name="jpcpDesign.depthToRigidFoundation.min"
                        value={parseFloat(parseFloat(convertLengthFt(data.jpcpDesign.depthToRigidFoundation.min)).toFixed(2))}
                        onChange={e => onDataChange({
                            target: {
                                name: "jpcpDesign.depthToRigidFoundation.min",
                                value: unitSystem === 'imperial'
                                    ? e.target.value
                                    : e.target.value / conversionFactors.ft_to_m
                            }
                        })}
                        onBlur={() => handleBlur(`${fieldPrefix}jpcpDesign.depthToRigidFoundation.min`, data.jpcpDesign.depthToRigidFoundation.min, 'number')}
                        isInvalid={getFieldState(`${fieldPrefix}jpcpDesign.depthToRigidFoundation.min`).isInvalid}
                    />
                </td>
                <td>
                    <Form.Control
                        type="number"
                        size="sm"
                        className="pd-input-sm"
                        name="jpcpDesign.depthToRigidFoundation.mean"
                        value={parseFloat(parseFloat(convertLengthFt(data.jpcpDesign.depthToRigidFoundation.mean)).toFixed(2))}
                        onChange={e => onDataChange({
                            target: {
                                name: "jpcpDesign.depthToRigidFoundation.mean",
                                value: unitSystem === 'imperial'
                                    ? e.target.value
                                    : e.target.value / conversionFactors.ft_to_m
                            }
                        })}
                        onBlur={() => handleBlur(`${fieldPrefix}jpcpDesign.depthToRigidFoundation.mean`, data.jpcpDesign.depthToRigidFoundation.mean, 'number')}
                        isInvalid={getFieldState(`${fieldPrefix}jpcpDesign.depthToRigidFoundation.mean`).isInvalid}
                    />
                </td>
                <td>
                    <Form.Control
                        type="number"
                        size="sm"
                        className="pd-input-sm"
                        name="jpcpDesign.depthToRigidFoundation.max"
                        value={parseFloat(parseFloat(convertLengthFt(data.jpcpDesign.depthToRigidFoundation.max)).toFixed(2))}
                        onChange={e => onDataChange({
                            target: {
                                name: "jpcpDesign.depthToRigidFoundation.max",
                                value: unitSystem === 'imperial'
                                    ? e.target.value
                                    : e.target.value / conversionFactors.ft_to_m
                            }
                        })}
                        onBlur={() => handleBlur(`${fieldPrefix}jpcpDesign.depthToRigidFoundation.max`, data.jpcpDesign.depthToRigidFoundation.max, 'number')}
                        isInvalid={getFieldState(`${fieldPrefix}jpcpDesign.depthToRigidFoundation.max`).isInvalid}
                    />
                </td>
                <td>
                    <Form.Select
                        size="sm"
                        className="pd-select-md"
                        name="jpcpDesign.depthToRigidFoundation.distribution"
                        value={data.jpcpDesign.depthToRigidFoundation.distribution}
                        onChange={onDataChange}>
                            <option value="Uniform" selected>Uniform</option>
                            <option value="Normal">Normal</option>
                            <option value="Lognormal">Lognormal</option>
                            <option value="Triangular">Triangular</option>
                    </Form.Select>
                </td>
            </tr>
            </tbody>
        </table>
    </div>
  );
}

export default PanelJPCP;